import api from 'config/api'

import { EUserLoanFlow } from '../loans/enum'

import {
  IFormattedEarning,
  IFormattedEarnEvent,
  ITotalAmountOfSavings,
  EarnWithdrawEstimateResponse,
  EarnWithdrawEstimateDto,
} from './enum'

export const createDeposit = async (params: {
  user_id?: string
  currency_code: string
  currency_network: string
  deposit: {
    expected_amount: string | number
  }
}): Promise<IFormattedEarning | string> => {
  try {
    const { data } = await api.post('/v2/earns/', {
      params: params,
    })
    return data.response
  } catch (err) {
    return err?.response?.message
  }
}

export const estimateDeposit = async (params: {
  currency_code: string
  currency_network: string
}): Promise<{
  annual_percent: string
}> => {
  try {
    const { data } = await api.get('/v2/earns/estimate', {
      params: params,
    })

    return data.response
  } catch (ignore) {
    return null
  }
}

export const confirmCreateDeposit = async ({
  earn_id,
  ...params
}: {
  user_id?: string
  earn_id: string
  agreed_to_tos?: boolean
  receive_from: EUserLoanFlow
}): Promise<boolean | string> => {
  try {
    const { data } = await api.post(`/v2/earns/${earn_id}/confirm`, {
      params: params,
    })
    return !!data.result
  } catch (err) {
    return err?.response?.message
  }
}

export const confirmCreateDepositFromAccount = async ({
  earn_id,
  ...params
}: {
  earn_id: string
  agreed_to_tos?: boolean
}): Promise<boolean> => {
  try {
    const { data } = await api.post(`/v2/earns/${earn_id}/confirm`, {
      params: { ...params, wallet: true },
    })
    return !!data.result
  } catch (ignore) {
    return false
  }
}

export const fetchUserDeposits = async (): Promise<IFormattedEarning[]> => {
  try {
    const { data } = await api.get('/v2/earns')
    return data?.response || []
  } catch (ignore) {
    return []
  }
}

export const _fetchDepositById = async ({ earn_id }: { earn_id: string }): Promise<IFormattedEarning> => {
  try {
    const { data } = await api.get(`/v2/earns/${earn_id}`)
    return data?.response
  } catch (ignore) {
    return null
  }
}

export const _fetchDepositEvents = async ({ earn_id }: { earn_id: string }): Promise<IFormattedEarnEvent[]> => {
  try {
    const { data } = await api.get(`/v2/earns/${earn_id}/events`)
    return data.response
  } catch (ignore) {
    return null
  }
}

export const increaseDepositAmount = async ({
  earn_id,
  amount,
  receive_from,
}: {
  earn_id: string
  amount: string
  receive_from: 'internal_wallet' | 'external_wallet'
}): Promise<
  | {
      result: boolean
      response: {
        address: string
        extraId: string
        isFallbackTx: boolean
        real_increase_amount: number
      }
    }
  | string
> => {
  try {
    const { data } = await api.post(`/v2/earns/${earn_id}/increase`, {
      params: {
        increase: {
          expected_amount: amount,
          receive_from,
        },
      },
    })

    return {
      result: data.result,
      response: data.response,
    }
  } catch (err) {
    return err?.response?.message
  }
}

export const increaseDepositAmountFromAccount = async ({
  earn_id,
  amount,
}: {
  earn_id: string
  amount: string
}): Promise<{
  result: boolean
  response: {
    address: string
    extraId: string
  }
}> => {
  try {
    const { data } = await api.post(`/v2/earns/${earn_id}/increase`, {
      params: {
        increase: {
          expected_amount: amount,
        },
        wallet: true,
      },
    })

    return {
      result: data.result,
      response: data.response,
    }
  } catch (ignore) {
    return null
  }
}

export const createWithdraw = async ({
  earn_id,
  ...params
}: {
  earn_id: string
  amount: string
  address?: string
  extra_id?: string
  verification_token?: string
  code_2fa?: string
  receive_to?: string
  add_to_whitelist: boolean
  address_name?: string
}): Promise<string | Record<string, string>> => {
  try {
    const { data } = await api.post(`/v2/earns/${earn_id}/withdraw`, {
      params: { ...params, wallet: true },
    })
    return data?.response
  } catch (err) {
    return err?.response?.message || err?.response?.error || ''
  }
}

export const createInternalWithdraw = async ({
  earn_id,
  ...params
}: {
  earn_id: string
  amount: string
  receive_to?: string
}): Promise<
  | boolean
  | {
      transactionId: string
    }
> => {
  try {
    const { data } = await api.post(`/v2/earns/${earn_id}/internal-withdraw`, {
      params: { ...params, wallet: true },
    })
    return data?.response
  } catch (e) {
    return false
  }
}

export const getTotalAmountOfSavings = async (): Promise<ITotalAmountOfSavings> => {
  try {
    const { data } = await api.get(`/v2/earns/summary`)

    return data.response
  } catch (e) {
    return null
  }
}

export const estimateWithdraw = async (dto: EarnWithdrawEstimateDto): Promise<EarnWithdrawEstimateResponse | string> => {
  try {
    const { data } = await api.post(`/v2/earns/withdraw/estimate`, {
      params: { ...dto },
    })

    return data.response
  } catch (e) {
    return e?.response?.message
  }
}
